import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useThreads, useCreateThread, useDeleteThread, useUpdateThread } from '@/hooks/use-query-threads';
import { Sidebar } from '@/components/chat/Sidebar';
import { useAuth } from '@/lib/auth';
import { SelectedThreadContext } from '@/App';
import { SidebarContext } from '@/contexts/SidebarContext';
import { useContext, useEffect, useRef } from 'react';
import { usePersistedState } from '@/hooks/use-persisted-state';
import useMediaQuery from '@/hooks/useMediaQuery';
import { useLastVisitedPage } from '@/hooks/use-last-visited-page';

export function PersistentLayout() {
  useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Active NextGen simulation runs (/nextgen/sim/:sectionNumber) own the full
  // viewport via NextGenExamLayout — the global sidebar would compete with the
  // exam icon rail. The /review subroute keeps the sidebar.
  const hideSidebar = /^\/nextgen\/sim\/\d+$/.test(location.pathname);
  const { isExpanded, setIsExpanded, setIsPinned: contextSetIsPinned } = useContext(SidebarContext);
  const { selectedThreadId, setSelectedThreadId } = useContext(SelectedThreadContext);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const isClickOutsideEnabled = useMediaQuery('(max-width: 675px)');


  // Use direct persisted state for sidebar pinning
  const [isPinned, setIsPinnedLocal] = usePersistedState<boolean>('sidebar-is-pinned', false);
  
  // Sync with context if available
  const setIsPinned = (value: boolean) => {
    setIsPinnedLocal(value);
    if (contextSetIsPinned) {
      contextSetIsPinned(value);
    }
  };
  
  // Update context when local state changes
  useEffect(() => {
    if (contextSetIsPinned) {
      contextSetIsPinned(isPinned);
    }
  }, [isPinned, contextSetIsPinned]);
  
  // Ref for the sidebar container to detect clicks outside
  const sidebarRef = useRef<HTMLDivElement>(null);
  
  // Use our custom hook to track the last visited page
  useLastVisitedPage();

  // Listen for focus mode event from NotesPage
  useEffect(() => {
    const handleFocusMode = (event: CustomEvent) => {
      if (event.detail?.enabled) {
        // Enter focus mode: unpin and collapse the sidebar
        setIsPinned(false);
        setIsExpanded(false);
      } else {
        // Exit focus mode: expand the sidebar (but don't pin it)
        setIsExpanded(true);
      }
    };

    window.addEventListener('focusMode', handleFocusMode as EventListener);
    return () => {
      window.removeEventListener('focusMode', handleFocusMode as EventListener);
    };
  }, [setIsExpanded, setIsPinned]);

  const threadQuery = useThreads();
  const threads = threadQuery.data || [];
  const createThreadMutation = useCreateThread();
  const updateThreadMutation = useUpdateThread();
  const deleteThreadMutation = useDeleteThread();

  const handleNewChat = async () => {
    try {
      const thread = await createThreadMutation.mutateAsync('New Conversation');
      if (thread) {
        setSelectedThreadId(thread.id);
        navigate(`/chat/${thread.id}`, { replace: true });
      }
    } catch (error) {
      console.error('Failed to create thread:', error);
    }
  };



  const handleDeleteThread = async (threadId: string) => {
    try {
      await deleteThreadMutation.mutateAsync(threadId);
    } catch (error) {
      console.error('Failed to delete thread:', error);
    }
  };

  const handleRenameThread = async (threadId: string, newTitle: string) => {
    try {
      await updateThreadMutation.mutateAsync({ id: threadId, title: newTitle });
    } catch (error) {
      console.error('Failed to rename thread:', error);
    }
  };

  const handleSetActiveThread = (threadId: string) => {
    setSelectedThreadId(threadId);
    navigate(`/chat/${threadId}`, { state: { fromSidebar: true } });
  };

  const handlePinChange = (pinned: boolean) => {
    setIsPinned(pinned);
    if (pinned) {
      setIsExpanded(true);
    }
  };

  // Handle click outside sidebar to minimize it at 675px width
  useEffect(() => {
    const handleClickOutside = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      // Ignore clicks inside Radix portals (dropdown menus, popovers) which
      // render outside the sidebar DOM tree. Without this, tapping a menu item
      // in SidebarUserMenu (Settings/Subscription/Support/Sign Out) fires
      // pointerdown "outside" the sidebar and dismisses both menus before the
      // click handler runs.
      if (
        target?.closest('[data-radix-popper-content-wrapper]') ||
        target?.closest('[role="menu"]')
      ) {
        return;
      }
      // Only handle click outside if:
      // 1. Screen width is 675px or less
      // 2. Sidebar is expanded
      // 3. Click is outside the sidebar
      // Note: On mobile/tablet, we close even if pinned for better UX
      if (
        isClickOutsideEnabled &&
        isExpanded &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        setIsExpanded(false);
        // On mobile/tablet, also unpin when clicking outside
        if (isPinned) {
          setIsPinned(false);
        }
      }
    };

    // Add event listener
    document.addEventListener('pointerdown', handleClickOutside);
    
    // Cleanup
    return () => {
      document.removeEventListener('pointerdown', handleClickOutside);
    };
  }, [isClickOutsideEnabled, isExpanded, isPinned, setIsExpanded, setIsPinned]);

  return (
    <div className="h-full flex flex-col">
      <div className="flex flex-1 min-h-0 bg-gray-50 dark:bg-gray-900">
        {!hideSidebar && isExpanded && !isDesktop && (
          <div
            className="fixed inset-0 bg-black/50 z-20"
            onClick={() => {
              setIsExpanded(false);
              if (isPinned) {
                setIsPinned(false);
              }
            }}
          />
        )}

        {!hideSidebar && (
          <div
            ref={sidebarRef}
            className={`${
              isExpanded || isDesktop ? 'translate-x-0' : '-translate-x-full'
            } ${
              isDesktop ? 'relative' : 'fixed'
            } ${isDesktop ? (isExpanded ? 'w-64' : 'w-16') : 'w-64'} h-full transition-all duration-300 ease-in-out z-50 flex-shrink-0`}
          >
            <Sidebar
              setActiveTab={handleSetActiveThread}
              isDesktopExpanded={isExpanded}
              onDesktopExpandedChange={setIsExpanded}
              isPinned={isPinned}
              onPinChange={handlePinChange}
              onNewChat={handleNewChat}
              onDeleteThread={handleDeleteThread}
              onRenameThread={handleRenameThread}
              sessions={threads.map(thread => ({
                id: thread.id,
                title: thread.title,
                created_at: thread.created_at
              }))}
              currentSession={selectedThreadId}
            />
          </div>
        )}

        <div className="flex-1 min-w-0 flex flex-col bg-gray-50 dark:bg-gray-900" style={{ zIndex: 1 }}>
          <Outlet />
        </div>
      </div>
    </div>
  );
}

export default PersistentLayout;